import { useState, useMemo, useCallback } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cancel01Icon } from '@hugeicons/core-free-icons'

import {
  useAdminAllUsers,
  useAdminSetPlatformAdmin,
} from '@/features/admin/services/admin.convex'
import { AuthGuard } from '@/features/auth'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/components/ui/multi-select'

export const Route = createFileRoute('/(dashboard)/admin/users/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Admin — Users',
    breadcrumbs: [{ title: 'Admin' }, { title: 'Users' }],
  }),
})

function RouteComponent() {
  const { data: users, isLoading } = useAdminAllUsers()
  const setPlatformAdmin = useAdminSetPlatformAdmin()
  const [search, setSearch] = useState('')
  const [adminFilter, setAdminFilter] = useState<string[]>([])
  const [orgFilter, setOrgFilter] = useState<string[]>([])

  const hasActiveFilters =
    search.length > 0 || adminFilter.length > 0 || orgFilter.length > 0

  const clearFilters = useCallback(() => {
    setSearch('')
    setAdminFilter([])
    setOrgFilter([])
  }, [])

  const allOrgNames = useMemo(() => {
    const names = new Set<string>()
    users.forEach((u) => u.orgNames.forEach((n) => names.add(n)))
    return Array.from(names).sort()
  }, [users])

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    return users.filter((u) => {
      if (
        term &&
        !(u.name ?? '').toLowerCase().includes(term) &&
        !(u.email ?? '').toLowerCase().includes(term) &&
        !(u.username ?? '').toLowerCase().includes(term)
      )
        return false
      if (adminFilter.length > 0) {
        const isAdmin = adminFilter.includes('admin')
        const isNonAdmin = adminFilter.includes('non-admin')
        if (isAdmin && isNonAdmin) {
          /* both selected = show all */
        } else if (isAdmin && !u.isPlatformAdmin) return false
        else if (isNonAdmin && u.isPlatformAdmin) return false
      }
      if (
        orgFilter.length > 0 &&
        !orgFilter.some((o) => u.orgNames.includes(o))
      )
        return false
      return true
    })
  }, [users, search, adminFilter, orgFilter])

  const handleToggleAdmin = async (userId: string, current: boolean) => {
    try {
      await setPlatformAdmin({
        userId: userId as never,
        isPlatformAdmin: !current,
      })
      toast.success(
        current ? 'Platform admin revoked' : 'Platform admin granted',
      )
    } catch {
      toast.error('Failed to update admin status')
    }
  }

  return (
    <AuthGuard requiresPlatformAdmin>
      <div className="space-y-6">
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Users</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isLoading
                ? 'Loading…'
                : `${users.length} user${users.length !== 1 ? 's' : ''} total`}
            </p>
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-6">
          <div className="relative w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-muted/50 border-b transition-colors">
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    User
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Organizations
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Platform Admin
                  </TableHead>
                  <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                    Actions
                  </TableHead>
                </TableRow>
                {/* Filter row */}
                <TableRow className="border-b bg-muted/20 hover:bg-muted/20">
                  <TableHead className="px-2 py-1.5">
                    <Input
                      placeholder="Search…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-8 bg-background text-sm font-normal"
                    />
                  </TableHead>
                  <TableHead className="px-2 py-1.5">
                    <MultiSelect
                      values={orgFilter}
                      onValuesChange={setOrgFilter}
                    >
                      <MultiSelectTrigger className="h-8 w-full bg-background text-sm font-normal">
                        <MultiSelectValue placeholder="All orgs…" />
                      </MultiSelectTrigger>
                      <MultiSelectContent>
                        <MultiSelectGroup>
                          {allOrgNames.map((name) => (
                            <MultiSelectItem key={name} value={name}>
                              {name}
                            </MultiSelectItem>
                          ))}
                        </MultiSelectGroup>
                      </MultiSelectContent>
                    </MultiSelect>
                  </TableHead>
                  <TableHead className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <MultiSelect
                        values={adminFilter}
                        onValuesChange={setAdminFilter}
                      >
                        <MultiSelectTrigger className="h-8 flex-1 bg-background text-sm font-normal">
                          <MultiSelectValue placeholder="All users…" />
                        </MultiSelectTrigger>
                        <MultiSelectContent>
                          <MultiSelectGroup>
                            <MultiSelectItem value="admin">
                              Platform admins
                            </MultiSelectItem>
                            <MultiSelectItem value="non-admin">
                              Non-admins
                            </MultiSelectItem>
                          </MultiSelectGroup>
                        </MultiSelectContent>
                      </MultiSelect>
                      {hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          className="h-8 w-8 p-0 text-muted-foreground shrink-0"
                        >
                          <HugeiconsIcon
                            icon={Cancel01Icon}
                            className="h-4 w-4"
                          />
                        </Button>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="px-2 py-1.5" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow
                      key={i}
                      className="hover:bg-muted/50 border-b transition-colors"
                    >
                      {Array.from({ length: 4 }).map((__, j) => (
                        <TableCell
                          key={j}
                          className="p-2 align-middle whitespace-nowrap"
                        >
                          <div className="h-4 bg-muted animate-pulse rounded w-24" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!isLoading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="p-2 align-middle text-center text-muted-foreground py-12"
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading &&
                  filtered.map((user) => (
                    <TableRow
                      key={user.id}
                      className="hover:bg-muted/50 border-b transition-colors"
                    >
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <AvatarWithInitials
                            name={user.name ?? user.email ?? user.id}
                            src={user.image ?? undefined}
                            className="h-8 w-8"
                          />
                          <div>
                            <div className="font-medium">
                              {user.name ?? 'Unnamed'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="p-2 align-middle">
                        <div className="flex flex-wrap gap-1">
                          {user.orgNames.length === 0 ? (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          ) : (
                            user.orgNames.map((name) => (
                              <Badge
                                key={name}
                                variant="secondary"
                                className="text-xs"
                              >
                                {name}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={
                                  !user.isInPlatformOrg
                                    ? 'cursor-not-allowed'
                                    : undefined
                                }
                              >
                                <Switch
                                  checked={user.isPlatformAdmin}
                                  disabled={!user.isInPlatformOrg}
                                  onCheckedChange={() =>
                                    handleToggleAdmin(
                                      user.id,
                                      user.isPlatformAdmin,
                                    )
                                  }
                                />
                              </span>
                            </TooltipTrigger>
                            {!user.isInPlatformOrg && (
                              <TooltipContent side="top">
                                Must be a member of the platform organization
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className="p-2 align-middle whitespace-nowrap">
                        <Button variant="outline" size="sm" asChild>
                          <Link
                            to="/admin/users/$userId/organizations"
                            params={{ userId: user.id }}
                            search={{ name: user.name ?? undefined }}
                          >
                            View Profile
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </AuthGuard>
  )
}
